import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, Bot } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article3 = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="outline" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.backToHome')}
            </Button>
          </Link>
          
          <div className="flex items-center mb-4">
            <Badge className="bg-green-600 text-white mr-4">
              <Bot className="h-3 w-3 mr-1" />
              {t('category.technology')}
            </Badge>
            <span className="text-sm text-gray-600">Kuehne+Nagel</span>
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            January 8, 2026
            <span className="mx-2">•</span>
            <span>7 min read</span>
          </div>
          
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.3.title')}
          </h1>
          
          <img 
            src="https://imgs.newseditor.aws.kuehne-nagel.cloud/13467df1-bff7-4496-8cd1-f2e5cda42a4c.jpg" 
            alt="AI-powered container loss detection system at sea"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.3.intro')}
            </p>

            <p className="mb-6">
              {t('article.3.detail5')}
            </p>

            <p className="mb-6">
              {t('article.3.section1.content')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.3.section1.title')}</h2>
            
            <p className="mb-6">
              {t('article.3.detail1')}
            </p>

            <p className="mb-6">
              {t('article.3.detail2')}
            </p>

            <blockquote className="border-l-4 border-green-500 pl-6 my-8 italic text-gray-700">
              "{t('article.3.quote1')}"
              <footer className="text-sm text-gray-600 mt-2">
                — {t('article.3.quote1.author')}
              </footer>
            </blockquote>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.3.section2.title')}</h2>
            
            <p className="mb-6">
              {t('article.3.section2.content')}
            </p>

            <blockquote className="border-l-4 border-blue-500 pl-6 my-8 italic text-gray-700">
              "With this new technology now ready to move on from simulation testing, we're taking a major step, real-world detection and environmental protection."
              <footer className="text-sm text-gray-600 mt-2">
                — Founder of Eyesea
              </footer>
            </blockquote>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.3.section3.title')}</h2>
            
            <p className="mb-6">
              {t('article.3.section3.content')}
            </p>

            <p className="mb-6">
              {t('article.3.detail6')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.3.section4.title')}</h2>
            
            <p className="mb-6">
              {t('article.3.section4.content')}
            </p>

            <p className="mb-6">
              {t('article.3.detail8')}
            </p>

            <div className="bg-green-50 p-6 rounded-lg mt-8">
              <h3 className="font-bold text-gray-900 mb-2">Technology Features</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li><strong>Computer Vision:</strong> Analyzes deck and bridge camera imagery</li>
                <li><strong>Machine Learning:</strong> Automatically identifies container loss events</li>
                <li><strong>Real-time Reporting:</strong> Instant transmission to authorities</li>
                <li><strong>Geo-tagging:</strong> Precise location data for incidents</li>
                <li><strong>IMO Compliance:</strong> Meets new mandatory reporting requirements</li>
                <li><strong>Environmental Protection:</strong> Faster response to marine incidents</li>
              </ul>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} Kuehne+Nagel official newsletter.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a 
              href="https://mykn.kuehne-nagel.com/news/article/ai-powered-system-set-to-transform-container" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.backToAllArticles')}
              </Button>
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article3;